import * as React from 'react'
import {ReactNode} from 'react'
import PPCheckboxGroup from '../../../../shared/forms/PPCheckboxGroup'
import {Messages} from '../../../../../Messages'
import PPNumericField from '../../../../shared/forms/PPNumericField'
import {HKConstants} from '../../../../../HKConstants'
import {Nullable} from '../../../../../app/TypeHelpers'
import {FirstLineTimeZoneWithMaxTime} from './FirstLineTimeZoneWithMaxTime'
import {HOURS, RESERVATION_TIME_UNIT_MINUTES} from '../../../../../CodeMaster'
import {AdminDefaultMediumButton} from '../../../../../styledComponents/adminPage/Buttons'
import {LeftRowFlexContainer} from '../../../../../styledComponents/FlexComponents'
import { Box, InputAdornment } from '@mui/material';
import {TimeZoneInputWithMaxTime} from './TimeZoneInputWithMaxTime'
import equal from 'fast-deep-equal'
import {numberOrNull} from '../../../../../stringHelper/stringHelpers'
import styled from 'styled-components'

export interface MaxReservationTimeByTimeZone {
    startHour: Nullable<HOURS>
    endHour: Nullable<HOURS>
    maxTime: Nullable<number>
}

export interface MaxReservationTimeInputFormProps {
    isMultipleMaxReservationTime: boolean
    singleMaxReservationTime: Nullable<number>
    multipleMaxReservationTimes: MaxReservationTimeByTimeZone[]
    multipleMaxReservationUnitMinute: Nullable<RESERVATION_TIME_UNIT_MINUTES>
}

export interface MaxReservationTimeInputFormPropsWithCallback extends MaxReservationTimeInputFormProps {
    onChangeIsMultipleMaxReservationTime: (isMultipleMaxReservationTime: boolean) => void
    onChangeSingleMaxReservationTime: (time: Nullable<number>) => void
    onChangeMultipleMaxReservationTimes: (timezones: MaxReservationTimeByTimeZone[]) => void
    onChangeMultipleMaxReservationUnitMinute: (minute: Nullable<RESERVATION_TIME_UNIT_MINUTES>) => void
}

export class MaxReservationTimeInputForm extends React.Component<MaxReservationTimeInputFormPropsWithCallback> {
    constructor(props: Readonly<MaxReservationTimeInputFormPropsWithCallback>) {
        super(props)

        this.handleChangeChecked = this.handleChangeChecked.bind(this)
        this.handleClickAllClear = this.handleClickAllClear.bind(this)
        this.handleChangeSingleMaxReservationTime = this.handleChangeSingleMaxReservationTime.bind(this)
        this.handleChangeStartHour = this.handleChangeStartHour.bind(this)
        this.handleChangeMinute = this.handleChangeMinute.bind(this)
        this.handleChangeStartTime = this.handleChangeStartTime.bind(this)
        this.handleChangeEndHour = this.handleChangeEndHour.bind(this)
        this.handleChangeMaxTime = this.handleChangeMaxTime.bind(this)
    }

    shouldComponentUpdate(nextProps: Readonly<MaxReservationTimeInputFormPropsWithCallback>): boolean {
        return !(this.props === nextProps || equal(this.props, nextProps))
    }

    handleChangeChecked(label: string, checked: boolean): void {
        return this.props.onChangeIsMultipleMaxReservationTime(checked)
    }

    handleClickAllClear(): void {
        this.props.onChangeMultipleMaxReservationUnitMinute(null)
        this.props.onChangeMultipleMaxReservationTimes([
            {startHour: null, endHour: null, maxTime: null},
            {startHour: null, endHour: null, maxTime: null},
            {startHour: null, endHour: null, maxTime: null},
            {startHour: null, endHour: null, maxTime: null},
            {startHour: null, endHour: null, maxTime: null},
        ])
    }

    handleChangeSingleMaxReservationTime(event: React.ChangeEvent<HTMLInputElement>): void {
        this.props.onChangeSingleMaxReservationTime(numberOrNull(event.target.value))
    }

    handleChangeStartHour(startHour: Nullable<HOURS>): void {
        const firstTimeZone: MaxReservationTimeByTimeZone = {startHour: startHour, endHour: null, maxTime: null}
        this.props.onChangeMultipleMaxReservationTimes([
            firstTimeZone,
            ...this.props.multipleMaxReservationTimes.slice(1),
        ])
    }

    handleChangeMinute(minuts: Nullable<RESERVATION_TIME_UNIT_MINUTES>): void {
        this.props.onChangeMultipleMaxReservationUnitMinute(minuts)
    }

    handleChangeStartTime(startHour: HOURS): void {
        const nextHour = this.calcHour(startHour, 1)

        const firstTimeZone: MaxReservationTimeByTimeZone = {startHour: startHour, endHour: nextHour, maxTime: null}
        const nextTimeZone: MaxReservationTimeByTimeZone = {startHour: nextHour, endHour: startHour, maxTime: null}
        this.props.onChangeMultipleMaxReservationTimes([
            firstTimeZone,
            nextTimeZone,
            ...this.props.multipleMaxReservationTimes.slice(2),
        ])
    }

    handleChangeEndHour = (index: number) => (endHour: HOURS): void => {
        const timeZones: MaxReservationTimeByTimeZone[] = this.props.multipleMaxReservationTimes
        const updateMaxTime: Nullable<number> = this.getUpdateMaxTime(
            timeZones[index].maxTime, timeZones[index].startHour, endHour,
        )
        const targetTimeZone: MaxReservationTimeByTimeZone = {
            startHour: timeZones[index].startHour,
            endHour: endHour,
            maxTime: updateMaxTime,
        }

        let nextTimeZone: MaxReservationTimeByTimeZone
        if (timeZones[0].startHour === timeZones[index].endHour) {
            nextTimeZone = {startHour: endHour, endHour: timeZones[0].startHour, maxTime: null}
        } else {
            const updateNextMaxTime: Nullable<number> = this.getUpdateMaxTime(
                timeZones[index + 1].maxTime, endHour, timeZones[index + 1].endHour,
            )

            nextTimeZone = {startHour: endHour, endHour: timeZones[index + 1].endHour, maxTime: updateNextMaxTime}
        }

        this.props.onChangeMultipleMaxReservationTimes([
            ...timeZones.slice(0, index),
            targetTimeZone,
            nextTimeZone,
            ...timeZones.slice(index + 2),
        ])
    }

    handleChangeMaxTime = (index: number) => (maxTime: Nullable<number>): void => {
        const {onChangeMultipleMaxReservationTimes, multipleMaxReservationTimes} = this.props

        onChangeMultipleMaxReservationTimes([
            ...multipleMaxReservationTimes.slice(0, index),
            {
                ...multipleMaxReservationTimes[index],
                maxTime: maxTime,
            },
            ...multipleMaxReservationTimes.slice(index + 1),
        ])
    }

    calcHour = (currentHour: Nullable<HOURS>, offset: number): Nullable<HOURS> => {
        if (currentHour === null) {
            return null
        }

        const currentHourNum: number = Number(currentHour.value)
        const calcedHourNum: number = ((currentHourNum + offset) + 24) % 24

        return HOURS.codeToEnumConstants(`${calcedHourNum}`)
    }

    calcInterval = (startHour: Nullable<HOURS>, endHour: Nullable<HOURS>): Nullable<number> => {
        if (startHour === null || endHour === null) {
            return null
        }

        const startHourNum: number = Number(startHour.value)
        let endHourNum: number = Number(endHour.value)

        if (endHourNum < startHourNum) {
            endHourNum = endHourNum + 24
        }

        return endHourNum - startHourNum
    }

    getUpdateMaxTime = (
        currentMaxTime: Nullable<number>,
        startHour: Nullable<HOURS>,
        endHour: Nullable<HOURS>,
    ): Nullable<number> => {
        if (currentMaxTime === null) {
            return null
        }

        const timeInterval: Nullable<number> = this.calcInterval(startHour, endHour)
        if (timeInterval === null) {
            return null
        }

        return (currentMaxTime > timeInterval) ? null : currentMaxTime
    }

    isFirstTimeZoneIndex = (index: number): boolean => {
        return index === 0
    }

    isLastTimeZoneIndex = (index: number): boolean => {
        return index === 4
    }

    renderTimeZones(): ReactNode {
        interface InputForOneLine {
            startHour: Nullable<HOURS>
            endHour: Nullable<HOURS>
            maxTime: Nullable<number>
            selectableEndHour: Nullable<HOURS>
        }

        return (
            this.props.multipleMaxReservationTimes
                .map((item: MaxReservationTimeByTimeZone, index, array): InputForOneLine => {
                    const firstStartHour: Nullable<HOURS> = array[0].startHour

                    let selectableEndHour: Nullable<HOURS> = null
                    if (item.endHour !== null) {
                        selectableEndHour = (firstStartHour !== item.endHour)
                            ? this.calcHour(array[index + 1].endHour, -1)
                            : firstStartHour
                    }

                    return {
                        startHour: item.startHour,
                        endHour: item.endHour,
                        selectableEndHour: selectableEndHour,
                        maxTime: item.maxTime,
                    }
                })
                .map((item: InputForOneLine, index): ReactNode => {
                    if (this.isFirstTimeZoneIndex(index)) {
                        return (
                            <FirstLineTimeZoneWithMaxTime
                                key={index}
                                startHour={item.startHour}
                                endHour={item.endHour}
                                minute={this.props.multipleMaxReservationUnitMinute}
                                selectableEndHour={item.selectableEndHour}
                                maxReservationTime={item.maxTime}
                                onChangeEndHour={this.handleChangeEndHour(index)}
                                onChangeMaxTime={this.handleChangeMaxTime(index)}
                                onChangeStartHour={this.handleChangeStartHour}
                                onChangeMinute={this.handleChangeMinute}
                                onChangeStartTime={this.handleChangeStartTime}
                            />
                        )
                    }

                    return (
                        <TimeZoneInputWithMaxTime
                            key={index}
                            startHour={item.startHour}
                            endHour={item.endHour}
                            minute={this.props.multipleMaxReservationUnitMinute}
                            selectableEndHour={item.selectableEndHour}
                            maxReservationTime={item.maxTime}
                            onChangeEndHour={this.handleChangeEndHour(index)}
                            onChangeMaxTime={this.handleChangeMaxTime(index)}
                            disabledEndHour={this.isLastTimeZoneIndex(index)}
                        />
                    )
                })
        )
    }

    render(): ReactNode {
        return (
            <>
                <PPCheckboxGroup
                    data={[{
                        label: '時間帯ごとに設定する',
                        checked: this.props.isMultipleMaxReservationTime,
                    }]}
                    hint={Messages.HFACA0005.toString()}
                    onChangeChecked={this.handleChangeChecked}
                />
                {
                    !this.props.isMultipleMaxReservationTime &&
                    <label>
                        <LeftRowFlexContainer>
                            最大
                            <MaxReservationTimeInput
                                InputProps={{
                                    inputProps: {
                                        min: 1,
                                        max: HKConstants.VMN.FAC.MAX_RESERVATION_TIME_LIMIT_HOUR,
                                    },
                                    endAdornment: <InputAdornment position="end">時間まで</InputAdornment>,
                                }}
                                value={this.props.singleMaxReservationTime || ''}
                                onChange={this.handleChangeSingleMaxReservationTime}
                            />
                        </LeftRowFlexContainer>
                    </label>
                }
                {
                    this.props.isMultipleMaxReservationTime &&
                    (
                        <>
                            <AdminDefaultMediumButton
                                onClick={this.handleClickAllClear}
                                size={'small'}
                            >
                                全てクリア
                            </AdminDefaultMediumButton>
                            <Box mt={'8px'}>
                                {this.renderTimeZones()}
                            </Box>
                        </>
                    )
                }
            </>
        )
    }
}

const MaxReservationTimeInput = styled(PPNumericField)`
  width: 120px;
`
